/*
 * Copyright (c) 2021. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import _ from 'lodash';
import { z } from 'zod';
import { componentIdSchema } from './core';
export var COST_DB_TYPES;
(function (COST_DB_TYPES) {
    COST_DB_TYPES["hv"] = "hv";
    COST_DB_TYPES["pv"] = "pv";
    COST_DB_TYPES["bess"] = "bess";
})(COST_DB_TYPES || (COST_DB_TYPES = {}));
export var Inequality;
(function (Inequality) {
    Inequality["LESS_THAN"] = ">";
    Inequality["GREATER_THAN"] = "<";
    Inequality["EQUAL_GREATER_THAN"] = ">=";
    Inequality["EQUAL_LESS_THAN"] = "<=";
})(Inequality || (Inequality = {}));
export const COST_DB_REGIONS = ['US', 'India', 'APAC'];
export const QUARTERS = _.range(1, 5).map(String);
export const getQuarterLabel = (option) => `Q${option}`;
export const hvCostCategoryLabelMap = {
    substation_procurement: 'Substation Procurement',
    substation_install: 'Substation Installation',
    gen_tie_procurement: 'Gen-tie Procurement',
    gen_tie_install: 'Gen-tie Installation',
    mpt_and_breakers: 'MPT & Breakers',
    utility_improvements: 'Utility Improvements',
};
const mvarCostItemLabelMap = {
    reactive_power_equipment_supply: 'Reactive Power Equipment Supply',
    reactive_power_equipment_install: 'Reactive Power Equipment Install',
};
const byFeederCountCostItemLabelMap = {
    mv_breakers: 'MV Breakers',
    mv_switches: 'MV Switches',
    mv_bus: 'MV Bus',
    mv_structural_steel: 'MV Structural Steel',
    mv_foundations: 'MV Foundations',
    control_building_and_relay_panel_supply: 'Control Building and Relay Panel Supply',
    mv_foundations_install: 'MV Foundation Install',
    mv_equipment_install: 'MV Equipment Install',
    mv_structural_steel_and_bus_install: 'MV Structural Steel and Bus Install',
    substation_testing_and_commissioning: 'Substation Testing & Commissioning',
};
const bySubstationAcreageCostItemLabelMap = {
    lighting_supply: 'Lighting Supply',
    grounding_and_ug_conduit_supply: 'Grounding and UG Conduit Supply',
    grounding_and_ug_conduit_install: 'Grading/Grounding and UG Conduit Install',
};
const bySubstationPerimeterCostItemLabelMap = {
    substation_fence: 'Substation Fence (Furnish and Install)',
};
const byGenTieLengthCostItemLabelMap = {
    foundations: 'Foundations',
    poles: 'Poles',
    conductors: 'Conductors',
    insulators_and_support_arms: 'Insulators and Support Arms',
    opgw: 'OPGW',
    foundations_install: 'Foundation Install',
    poles_install: 'Pole Installation',
    insulators_and_support_arms_install: 'Insulators and Support Arms Installation',
    conductor_stringing_sagging_clipping: 'Conductor Stringing, Sagging, Clipping',
};
const byMptCountCostItemLabelMap = {
    hv_breakers: 'HV Breakers',
    hv_switches: 'HV Switches',
    hv_bus: 'HV Bus',
    hv_structural_steel: 'HV Structural Steel',
    hv_foundation_install: 'HV Foundation Install',
    hv_equipment_install: 'HV Equipment Install',
    hv_structural_steel_and_bus_install: 'HV Structural Steel and Bus Install',
};
const byMvaCapacityCostItemLabelMap = {
    mpt: 'MPT',
};
const fixedCostItemLabelMap = {
    above_ground_bos_supply: 'Above Ground BOS Supply (Arrestors, CCVTs, SSVT)',
    substation_mobilization: 'Substation Mobilization',
    grading_civil_works: 'Grading/Civil Works (Finish Rock Included)',
    termination_control_building: 'Set and Termination Control Building',
    civil: 'Civil (Access Roads, Fence Crossing, Stream Crossing, Etc.)',
    transmission_mobilization: 'Transmission Mobilization',
    commissioning: 'Commissioning',
    utility_improvements: 'Utility Improvements',
};
export const hvCostItemsLabelMap = {
    ...mvarCostItemLabelMap,
    ...byFeederCountCostItemLabelMap,
    ...bySubstationPerimeterCostItemLabelMap,
    ...bySubstationAcreageCostItemLabelMap,
    ...byGenTieLengthCostItemLabelMap,
    ...byMptCountCostItemLabelMap,
    ...byMvaCapacityCostItemLabelMap,
    ...fixedCostItemLabelMap,
};
export const hvUnitOptions = [
    'by_mvar_capacity',
    'by_feeder_count',
    'by_substation_acreage',
    'by_substation_perimeter',
    'by_gen_tie_length',
    'by_mpt_count',
    'by_mva_capacity',
    'fixed_costs',
];
export const baseCostSchema = z
    .object({
    variable: z.number().optional(),
    fixed: z.number().nullable().optional(),
    code: z
        .custom((val) => (typeof val === 'string' ? /^CONS-\d+$/.test(val) : false))
        .nullable()
        .optional(),
    name: z.string(),
    category: z.string().nullable(),
    perc_labor: z.number().nullable().optional(),
    notes: z.string().nullable().optional(),
})
    .strict();
export const genericCostSchema = baseCostSchema
    .extend({
    is_steel: z.boolean().optional(),
    is_conductor: z.boolean().optional(),
})
    .strict();
export const getDiscretionaryCostSchema = (units) => z.object({
    ...baseCostSchema.shape,
    ...componentIdSchema.shape,
    unit: z.enum(units),
});
const baseHvCostingSchema = z.strictObject({
    by_mvar_capacity: z.array(genericCostSchema),
    by_feeder_count: z.array(genericCostSchema),
    by_substation_acreage: z.array(genericCostSchema),
    by_substation_perimeter: z.array(genericCostSchema),
    by_gen_tie_length: z.array(genericCostSchema),
    by_mpt_count: z.array(genericCostSchema),
    by_mva_capacity: z.array(genericCostSchema),
    fixed_costs: z.array(genericCostSchema),
});
export const hvCostingSchema = baseHvCostingSchema.extend({
    discretionary_costs: z.array(getDiscretionaryCostSchema(hvUnitOptions)).optional(),
});
//# sourceMappingURL=hv-costing.js.map